import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { Skeleton, Stack } from "@mui/material";

import { useWindowSize } from "../../../utils/useWindowSize";
import { lastSelectedKeywordsCookieID } from "../../../interface/MainInterface";
import { HttpUnAuthApi } from "../../../interface/unauth-api";
import { CommonUtils } from "../../../utils/common_utils";
import LoadingCircle from "../../../utils/LoadingCircle";

// CSS
import "./KeywordNavigation.css";
import MonthltyKeywordChart from "../../report/chart/MonthltyKeywordChart";
import KeywordCategoryChart from "../../report/chart/KeywordCategoryChart";

interface propsType {
  selectedPeriod: string;
  selectedCategory: any;
  selectedKeywords: any;
  callback: any;
}

interface keywordInfoType {
  pk: string;
  sk: string;
  ai_catagory: string;
  count: number;
}

const unAuthAPI = new HttpUnAuthApi();

const KeywordNavigation = (props: propsType) => {
  const windowSize = useWindowSize();
  const cUtils = new CommonUtils();

  const [isLoading, setIsLoading] = useState(false);
  const [keywords, setKeywords] = useState<any>([]);
  const [keywordList, setKeywordList] = useState<keywordInfoType[] | null>();
  const [showFlow, setShowFlow] = useState(false); // 키워드 흐름 버튼 제어용

  const [keywordChartData, setKeywordChartData] = useState<any>(null);
  const [monthlyChartData, setMonthlyChartData] = useState<any>(null);

  useEffect(() => {
    return () => {
      setIsLoading(false);
      setKeywords([]);
      setShowFlow(false);
      setKeywordChartData(null);
      setMonthlyChartData(null);
    };
  }, []);

  // 키워드 조회 후 쿠키에 저장된 관심 키워드를 초기 세팅
  useEffect(() => {
    const checkCookies = () => {
      const cookieLastKeywords: any = window.localStorage.getItem(lastSelectedKeywordsCookieID);
      if (cookieLastKeywords) {
        let temp: any = [];
        for (const key of cookieLastKeywords.split(",")) {
          if (keywords.indexOf(key) > -1) {
            temp.push(key);
          } //<- 이걸하면 최신, 일주일, 한달, 분기 왔다갔다할때 저장한 키워드가 사라진다..
          // temp.push(key);
        }
        props.callback({ command: "selected_keyword", value: temp });
      }
    };

    // const getMonthlyFlowDatas = () => {
    //   const dataMap: { [pk: string]: { [month: string]: number } } = {};
    //   const monthSet = new Set<string>();

    //   keywordList?.forEach(({ pk, sk }) => {
    //     const year = sk.substring(0, 4);
    //     const month = sk.substring(4, 6);
    //     const formattedMonth = `${parseInt(month)}월`;

    //   if (!dataMap[pk]) {
    //     dataMap[pk] = {};
    //   }

    //   if (!dataMap[pk][formattedMonth]) {
    //     dataMap[pk][formattedMonth] = 0;
    //   }

    //   dataMap[pk][formattedMonth] += 1;
    //   monthSet.add(formattedMonth);
    //   })

    //   // 월을 정렬된 배열로 변환
    //   const sortedMonths = Array.from(monthSet).sort((a, b) => parseInt(a) - parseInt(b));

    //   const datasets = Object.keys(dataMap)
    //     .map((pk) => {
    //       const data = sortedMonths.map((month) => dataMap[pk][month] || 0);
    //       return { label: pk, data };
    //   });

    //   setMonthlyChartData({datasets, labels: sortedMonths});
    // }

    // 월별로 데이터 그룹화하는 함수
    const groupDataByMonthAndPk = (data: any) => {
      const result: Record<string, Record<string, number>> = {};

      data.forEach((item: any) => {
        const month = item.sk.substring(0, 6); // sk에서 연월(YYYYMM)을 추출
        const { pk, count } = item;

        if (!result[month]) {
          result[month] = {};
        }

        if (!result[month][pk]) {
          result[month][pk] = 0;
        }

        result[month][pk] += count;
      });

      return result;
    };

    // 주차 계산 함수
    const getWeekOfMonth = (date: Date) => {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1); // 해당 월의 첫 번째 날
      const dayOfMonth = date.getDate();
      const dayOfWeek = firstDay.getDay();
      return Math.ceil((dayOfMonth + dayOfWeek) / 7); // 월 기준으로 주차 계산
    };

    // 일별, 주별, 월별로 데이터 그룹화하는 함수
    const groupDataByPeriod = (data: any, period: string) => {
      const result: Record<string, Record<string, number>> = {};

      data.forEach((item: any) => {
        let periodKey = "";

        if (period === "recent" || period === "week") {
          periodKey = item.sk.substring(0, 8); // YYYYMMDD (일별)
        } else if (period === "month") {
          const year = item.sk.substring(0, 4);
          const month = item.sk.substring(4, 6);
          const day = item.sk.substring(6, 8);
          const date = new Date(`${year}-${month}-${day}`);
          const weekNumber = getWeekOfMonth(date); // 주차 계산 함수
          periodKey = `${year}${month}0${weekNumber}`; // MMWW (주별)
        } else if (period === "quarter") {
          periodKey = item.sk.substring(0, 6); // YYYYMM (월별)
        }

        const { pk, count } = item;
        const keyword = pk.split("#")[1];

        if (!result[periodKey]) {
          result[periodKey] = {};
        }

        if (!result[periodKey][keyword]) {
          result[periodKey][keyword] = 0;
        }

        result[periodKey][keyword] += count;
      });

      return result;
    };

    // pk별 총 count를 계산하는 함수
    const calculateTotalCounts = (groupedData: Record<string, Record<string, number>>) => {
      const totalCounts: Record<string, number> = {};

      Object.values(groupedData).forEach((monthData) => {
        Object.entries(monthData).forEach(([pk, count]) => {
          if (!totalCounts[pk]) {
            totalCounts[pk] = 0;
          }
          totalCounts[pk] += count;
        });
      });

      return totalCounts;
    };

    const getMonthlyFlowDatas = (groupedData: Record<string, Record<string, number>>) => {
      let labels: string[] = [];
      let groupedByPeriodData: Record<string, Record<string, number>> = {};

      if (props.selectedPeriod === "recent" || props.selectedPeriod === "week") {
        labels = Object.keys(groupedData).map((date) => `${date.substring(6, 8)}일`);
        groupedByPeriodData = groupedData;
      } else if (props.selectedPeriod === "month") {
        labels = Object.keys(groupedData).map((date) => `${date.substring(4, 6)}월 ${date.substring(6, 8)}주차`);
        groupedByPeriodData = groupedData;
      } else if (props.selectedPeriod === "quarter") {
        labels = Object.keys(groupedData).map((month) => `${month.substring(4, 6)}월`);
        groupedByPeriodData = groupedData;
      }
      // const labels = Object.keys(groupedData).map(month => `${month.substring(4, 6)}월`); // 월별 라벨 (YYYYMM)
      // const pks = Array.from(new Set(Object.values(groupedData).flatMap(data => Object.keys(data)))); // 모든 pk 추출

      const totalCounts = calculateTotalCounts(groupedData);
      const top10Pks = Object.entries(totalCounts)
        .sort(([, a], [, b]) => b - a) // count 내림차순 정렬
        .slice(0, 10) // 상위 10개 선택
        .map(([pk]) => pk); // pk만 추출

      const datasets = top10Pks.map((pk) => ({
        label: pk,
        data: labels.map((_, index) => {
          const originalMonth = Object.keys(groupedData)[index]; // 원래의 YYYYMM 값
          return groupedData[originalMonth][pk] || 0;
        }),
        fill: false,
      }));

      const data = {
        labels,
        datasets,
      };

      setMonthlyChartData(data);
    };

    const getKeywordBarDatas = () => {
      // pk 별로 ai_category별 count를 그룹화
      const chartData = keywordList?.reduce((acc, item) => {
        if (item.ai_catagory === "design") {
          return acc;
        }
        const pk = item.pk.split("#")[1];
        if (!acc[pk]) {
          acc[pk] = {};
        }
        if (!acc[pk][item.ai_catagory]) {
          acc[pk][item.ai_catagory] = 0;
        }
        acc[pk][item.ai_catagory] += item.count;
        return acc;
      }, {} as Record<string, Record<string, number>>);

      // pk별 총 count를 계산하고 상위 5개만 선택
      const pkTotals = Object.keys(chartData || {}).map((pk) => {
        const total = Object.values(chartData?.[pk] || {}).reduce((sum, value) => sum + value, 0);
        return { pk, total };
      });

      // 총 count 값으로 정렬하여 상위 5개 pk를 선택
      const top5Pk = pkTotals
        .sort((a, b) => b.total - a.total)
        .slice(0, 5)
        .map((item) => item.pk);

      // 차트 라벨과 데이터셋 추출 (상위 5개의 pk만 사용)
      const labels = top5Pk;
      const aiCategories = Array.from(
        new Set(
          keywordList?.filter((item) => item.ai_catagory !== "design")
            .map((item) => item.ai_catagory)
        )
      );

      const datasets = aiCategories.map((category) => ({
        label: category,
        data: labels.map((label) => chartData?.[label]?.[category] || 0),
        backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(
          Math.random() * 255
        )}, 0.5)`,
        maxBarThickness: 23,
      }));

      const data = {
        axis: "y",
        labels,
        datasets,
      };

      setKeywordChartData(data);
    };

    if (keywords.length > 0) {
      checkCookies();
      getMonthlyFlowDatas(groupDataByPeriod(keywordList, props.selectedPeriod));
      getKeywordBarDatas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywords]);

  // 카테고리, 검색기간 변경시 조회
  useEffect(() => {
    if (props.selectedPeriod !== "") get_keyword_list();
  }, [props.selectedCategory, props.selectedPeriod]);

  // 키워드 리스트 조회
  const get_keyword_list = async () => {
    const param: any = {
      command: "get_keyword_list",
      gubun: "keyword_count",
      category: props.selectedCategory === null ? "all" : props.selectedCategory.key,
    };

    setKeywords([]);

    // 검색 조건별 날짜 세팅
    const search_dtm = _make_search_dtm();
    param.from_dtm = search_dtm.from;
    param.to_dtm = search_dtm.to;

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      setKeywordList(res.response.keyword_list);
      const result = _make_keyword_list(res.response.keyword_list);
      const shuffledResult = shuffle_list(result);
      setKeywords(shuffledResult);
    } else {
      console.error("[ERROR] get_keyword_list : ", res.response.error_msg);
    }
  };

  const shuffle_list = (arr: []) => {
    const shuffledList = [...arr];
    for (let i = shuffledList.length -1; i > 0; i--) {
      const randomIndex = Math.floor(Math.random() * (i + 1));
      [shuffledList[i], shuffledList[randomIndex]] = [shuffledList[randomIndex], shuffledList[i]];
    }
    return shuffledList;
  }

  // 키워드 선택 이벤트
  const selectKeyword = (keyword: string) => {
    let temp: any = [];
    if (props.selectedKeywords !== null) {
      temp = [...props.selectedKeywords];
    }
    const index = temp.indexOf(keyword);
    if (index > -1) {
      temp.splice(index, 1);
    } else {
      temp = [];
      temp.push(keyword);
    }
    props.callback({ command: "selected_keyword", value: temp });
  };

  // 화면 사이즈에 맞게 키워드 펼치기/접기 설정
  useEffect(() => {
    const toggleButton = document.getElementById("kwToggleBtn");
    const tabs: any = document.querySelectorAll(".kw-option:nth-child(n+10)");
    if (toggleButton)
      if (windowSize.width > 1023) {
        tabs.forEach((tab: any) => (tab.style.display = "inline-block"));
        toggleButton.style.display = "none";
      } else {
        tabs.forEach((tab: any) => (tab.style.display = "none"));
        document.querySelectorAll(".kw-option:nth-child(-n+9)").forEach((tab: any) => (tab.style.display = "inline-block"));
        toggleButton.style.display = "block";
        toggleButton.innerText = "+ 전체보기";
      }
  }, [windowSize]);

  // 키워드 펼치기/접기 버튼 클릭시 이벤트
  const toggleExpand = () => {
    const toggleButton = document.getElementById("kwToggleBtn");
    const tabs: any = document.querySelectorAll(".kw-option:nth-child(n+10)");
    if (toggleButton)
      if (toggleButton.innerText === "+ 전체보기") {
        tabs.forEach((tab: any) => (tab.style.display = "inline-block"));
        toggleButton.innerText = "- 접기";
      } else {
        tabs.forEach((tab: any) => (tab.style.display = "none"));
        toggleButton.innerText = "+ 전체보기";
      }
  };

  //--------------------------------------------------
  // Sub module functions
  //--------------------------------------------------
  // 검색 조건별 날짜 계산
  const _make_search_dtm = () => {
    let from_dtm = "";
    let to_dtm = "";
    // 검색 조건별 날짜 세팅
    if (props.selectedPeriod === "recent") {
      // 3일전
      from_dtm = dayjs().subtract(3, "day").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    } else if (props.selectedPeriod === "week") {
      // 1주일
      from_dtm = dayjs().subtract(7, "day").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    } else if (props.selectedPeriod === "month") {
      // 1주일
      from_dtm = dayjs().subtract(1, "month").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    } else if (props.selectedPeriod === "quarter") {
      // 3개월
      from_dtm = dayjs().subtract(3, "month").format("YYYYMMDD");
      to_dtm = dayjs().add(1, "day").format("YYYYMMDD");
    }
    return { from: from_dtm, to: to_dtm };
  };

  // 키워드별 머지 및 정렬(많은 히트수)
  const _make_keyword_list = (list: any) => {
    let result = cUtils.mergeJsonList(list, "pk", "count");
    result = cUtils.jsonListSort(result, "count");

    let temp: any = [];
    for (const key of result) {
      if (temp.length > 26) break;
      // temp.push(key.pk);
      const keyword = key.pk.split("#")[1];
      if (temp.includes(keyword)) continue;
      else temp.push(keyword);
    }

    return temp;
  };

  function getTargetPosition() {
    if (window.innerWidth <= 1023) {
      return 600; // 화면 크기가 768px 이하일 때
    } else {
      return 580; // 화면 크기가 769px 이상일 때
    }
  }

  const showScrollPosition = 800; // 버튼이 나타날 스크롤 위치 (px)
  let lastScrollTop = 0;

  const scrollEvent = () => {
    const scrollButton = document.getElementById("goKeyword");

    let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > showScrollPosition) {
      // 스크롤 위치가 showScrollPosition을 넘을 때
      scrollButton?.classList.add("show");
    } else {
      // 스크롤 위치가 showScrollPosition 이하일 때
      scrollButton?.classList.remove("show");
    }
    lastScrollTop = scrollTop;
  };

  useEffect(() => {
    window.addEventListener("scroll", scrollEvent);
    return () => {
      window.removeEventListener("scroll", scrollEvent);
    };
  }, []);

  const scrollToKeyword = () => {
    const targetPosition = getTargetPosition();
    window.scrollTo({
      top: targetPosition, // 설정한 위치로 이동
      behavior: "smooth", // 부드럽게 스크롤
    });
  };

  return (
    <>
      <article className="article-02 no-margin">
        <div className="article-tit">
          키워드로 보는 트렌드
          <div className="kwsel-btn-wrap">
            <button
              className="btn-kw-ref"
              onClick={() => {
                props.callback({ command: "selected_keyword", value: [] });
              }}
              style={{ marginRight: "18px" }}
            >
              선택 해제<i className="show-more"></i>
            </button>
            <button
              className="btn-kw-flow"
              onClick={() => {
                setShowFlow(!showFlow);
              }}
            >
              키워드 흐름 보기<i className="show-more"></i>
            </button>
          </div>
        </div>

        <div className="keyword-wrap">
          <div className="keywords">
            {keywords.length > 0
              ? keywords.map((keyword: string, index: number) => (
                  <button
                    key={`keyword-${index}`}
                    className={props.selectedKeywords?.indexOf(keyword) > -1 ? "kw-option kw-active" : "kw-option"}
                    onClick={() => {
                      selectKeyword(keyword);
                    }}
                  >
                    {keyword}
                  </button>
                ))
              : [...Array(windowSize.width > 1023 ? 27 : 9)].map((value: any, index: number) => (
                  <Skeleton
                    key={`keyword-${index}`}
                    variant="rounded"
                    className={
                      windowSize.width > 1023
                        ? (index + 1) % 9 === 0
                          ? "kw-skeleton mr-0"
                          : "kw-skeleton"
                        : (index + 1) % 3 === 0
                        ? "kw-skeleton mr-0"
                        : "kw-skeleton"
                    }
                  />
                ))}
          </div>
          <button id="kwToggleBtn" className="kw-all-show" onClick={toggleExpand}>
            + 전체보기
          </button>
          <div className={showFlow ? "keyword-flow show-flow" : "keyword-flow"}>
            <Stack direction="column" gap={3}>
              <div>
                <p>키워드별 카테고리 분포</p>
                <div className="chart-wrap">{monthlyChartData && <KeywordCategoryChart chartData={keywordChartData} />}</div>
              </div>
              <div>
                <p>월별 키워드 언급량 추이</p>
                <div className="chart-wrap">{monthlyChartData && <MonthltyKeywordChart chartData={monthlyChartData} />}</div>
              </div>
            </Stack>
          </div>
        </div>
      </article>
      <button id="goKeyword" className="go-top" onClick={scrollToKeyword}>
        <img src="/images/icon-arr-top.png" alt="" />
        <span>키워드 다시 선택하기</span>
      </button>
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default KeywordNavigation;
